import { log } from './logger';

/**
 * Timezone utility functions for displaying times in both event and user timezones
 */

/**
 * Format a TIMESTAMPTZ to show both event timezone and user's local timezone
 * @param {string} timestamptz - The TIMESTAMPTZ string from database
 * @param {string} eventTimezone - The timezone where the event is happening (e.g., 'America/New_York')
 * @returns {Object} - { date, time, eventTime, userTime }
 */
export const formatDualTimezone = (timestamptz, eventTimezone = 'America/New_York') => {
  if (!timestamptz) return { date: 'Not specified', time: '', eventTime: '', userTime: '' };

  try {
    const date = new Date(timestamptz);

    // Get user's local timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Format date in event timezone
    const eventDateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    const dateStr = eventDateFormatter.format(date);

    // Format time in event timezone
    const eventTimeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
    const eventTimeParts = eventTimeFormatter.formatToParts(date);
    const eventTimeStr = `${eventTimeParts.find(p => p.type === 'hour').value}:${eventTimeParts.find(p => p.type === 'minute').value} ${eventTimeParts.find(p => p.type === 'dayPeriod').value} ${eventTimeParts.find(p => p.type === 'timeZoneName').value}`;

    // Determine if we should show both timezones
    // Only show both timezones if user is in a different timezone than the event
    const shouldShowBoth = userTimezone !== eventTimezone;

    // If user is in the same timezone as the event, don't show dual timezone
    let combinedTimeStr = eventTimeStr;
    let userTimeStr = '';

    if (shouldShowBoth) {
      const userTimeFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });

      const userTimeParts = userTimeFormatter.formatToParts(date);
      userTimeStr = `${userTimeParts.find(p => p.type === 'hour').value}:${userTimeParts.find(p => p.type === 'minute').value} ${userTimeParts.find(p => p.type === 'dayPeriod').value} ${userTimeParts.find(p => p.type === 'timeZoneName').value}`;

      combinedTimeStr = `${eventTimeStr} (${userTimeStr})`;
    }

    return {
      date: dateStr,
      time: combinedTimeStr,
      eventTime: eventTimeStr,
      userTime: userTimeStr
    };
  } catch (err) {
      // Error caught and ignored
    log.error('Error formatting dual timezone:', err);
    return { date: 'Invalid date', time: '', eventTime: '', userTime: '' };
  }
};

/**
 * Get timezone abbreviation for a given timezone
 * @param {string} timezone - IANA timezone identifier
 * @param {Date} date - Date to get abbreviation for (affects DST)
 * @returns {string} Timezone abbreviation
 */
export const getTimezoneAbbreviation = (timezone, date = new Date()) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });

    const parts = formatter.formatToParts(date);
    return parts.find(part => part.type === 'timeZoneName')?.value || timezone;
  } catch (err) {
      // Error caught and ignored
    log.warn('Unable to get timezone abbreviation for', timezone, err);
    return timezone;
  }
};

/**
 * Get just the time portion (without timezone) for use in ranges like "2:00 PM - 3:00 PM EDT"
 * @param {string} timestamptz - The TIMESTAMPTZ string from database
 * @param {string} eventTimezone - The timezone where the event is happening
 * @returns {string} - Time without timezone (e.g., "3:00 PM")
 */
export const formatTimeOnly = (timestamptz, eventTimezone = 'America/New_York') => {
  if (!timestamptz) return '';
  
  try {
    const date = new Date(timestamptz);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return formatter.format(date);
  } catch (err) {
      // Error caught and ignored
    log.error('Error formatting time only:', err);
    return '';
  }
};

/**
 * Check if user is in a different timezone than the event
 * @param {string} eventTimezone - The timezone where the event is happening
 * @returns {boolean} - True if user is in different timezone
 */
export const isUserInDifferentTimezone = (eventTimezone) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return userTimezone !== eventTimezone;
};
